import React, { useState } from 'react';
import { Search, TrendingUp, Building2, Zap, Car, Pill, Factory, Smartphone, Banknote, ShoppingCart, ArrowUp, ArrowDown, Activity, Calendar, BarChart3 } from 'lucide-react';

const StockitySectorFinder = () => {
  const [selectedSector, setSelectedSector] = useState('');
  const [activeTab, setActiveTab] = useState('sectors'); // 'sectors' or 'scalping'

  // Today's market performance data
  const marketOverview = {
    nifty50: { value: 24813.30, change: -86.70, changePercent: -0.35 },
    sensex: { value: 81257.22, change: -299.48, changePercent: -0.37 },
    banknifty: { value: 50245.85, change: -125.40, changePercent: -0.25 },
    date: 'July 31, 2025',
    time: '15:30 IST'
  };

  // Scalping data - real-time gainers and losers
  const scalpingData = {
    nifty50: {
      value: 24764.20,
      change: -90.85,
      changePercent: -0.37,
      timestamp: 'As on 31 Jul, 2025 03:29:59 PM'
    },
    gauge: {
      gainers: 13,
      losers: 37,
      total: 50
    },
    topGainers: [
      { symbol: 'HINDUNILVR', rate: 2524.00, change: 86.6, changePercent: 3.55, weightPoints: 18.2 },
      { symbol: 'ITC', rate: 411.70, change: 41, changePercent: 1.01, weightPoints: 10.45 },
      { symbol: 'KOTAKBANK', rate: 1980.00, change: 20.3, changePercent: 1.04, weightPoints: 6.03 },
      { symbol: 'JSWSTEEL', rate: 1057.90, change: 20, changePercent: 1.93, weightPoints: 4.06 },
      { symbol: 'ICICIBANK', rate: 1485.10, change: 2.7, changePercent: 0.18, weightPoints: 3.74 },
      { symbol: 'POWERGRID', rate: 291.25, change: 2.3, changePercent: 0.8, weightPoints: 2.65 },
      { symbol: 'APOLLOHOSP', rate: 7507.00, change: 57, changePercent: 0.77, weightPoints: 1.24 },
      { symbol: 'NESTLEIND', rate: 2246.00, change: 14.5, changePercent: 0.65, weightPoints: 1.21 },
      { symbol: 'BRITANNIA', rate: 5765.00, change: 18, changePercent: 0.31, weightPoints: 0.48 },
      { symbol: 'TECHM', rate: 1464.00, change: 0.8, changePercent: 0.05, weightPoints: 0.12 },
      { symbol: 'GRASIM', rate: 2760.00, change: 1.4, changePercent: 0.05, weightPoints: 0.11 },
      { symbol: 'SBILIFE', rate: 1840.00, change: 0.9, changePercent: 0.05, weightPoints: 0.08 },
      { symbol: 'HEROMOTOCO', rate: 4251.90, change: 0.6, changePercent: 0.01, weightPoints: 0.01 }
    ],
    topLosers: [
      { symbol: 'TATACONSUM', rate: 1072.80, change: -0.3, changePercent: -0.03, weightPoints: -0.04 },
      { symbol: 'HDFCLIFE', rate: 757.00, change: -0.45, changePercent: -0.06, weightPoints: -0.11 },
      { symbol: 'SHRIRAMFIN', rate: 631.80, change: -1, changePercent: -0.16, weightPoints: -0.32 },
      { symbol: 'ULTRACEMCO', rate: 12250.00, change: -18, changePercent: -0.15, weightPoints: -0.43 },
      { symbol: 'EICHERMOT', rate: 5463.50, change: -17.5, changePercent: -0.32, weightPoints: -0.49 },
      { symbol: 'INDUSINDBK', rate: 798.00, change: -3.9, changePercent: -0.49, weightPoints: -0.78 },
      { symbol: 'BAJAJFINSV', rate: 1952.00, change: -7.8, changePercent: -0.4, weightPoints: -0.87 },
      { symbol: 'CIPLA', rate: 1552.00, change: -7.4, changePercent: -0.47, weightPoints: -0.91 },
      { symbol: 'TATAMOTORS', rate: 666.45, change: -2, changePercent: -0.3, weightPoints: -1.21 },
      { symbol: 'BAJAJ-AUTO', rate: 8003.00, change: -40.5, changePercent: -0.5, weightPoints: -1.24 },
      { symbol: 'WIPRO', rate: 248.25, change: -1.95, changePercent: -0.78, weightPoints: -1.39 },
      { symbol: 'TRENT', rate: 5024.00, change: -19.5, changePercent: -0.39, weightPoints: -1.41 },
      { symbol: 'ONGC', rate: 240.26, change: -1.55, changePercent: -0.64, weightPoints: -1.51 },
      { symbol: 'HINDALCO', rate: 683.70, change: -5.1, changePercent: -0.74, weightPoints: -1.69 },
      { symbol: 'M&M', rate: 3197.50, change: -11.4, changePercent: -0.36, weightPoints: -2 },
      { symbol: 'BEL', rate: 383.00, change: -3.5, changePercent: -0.91, weightPoints: -2.12 },
      { symbol: 'BAJFINANCE', rate: 880.00, change: -4.85, changePercent: -0.55, weightPoints: -2.41 },
      { symbol: 'COALINDIA', rate: 375.95, change: -3.95, changePercent: -1.04, weightPoints: -2.42 },
      { symbol: 'MARUTI', rate: 12521.00, change: -97, changePercent: -0.77, weightPoints: -2.56 }
    ]
  };

  const sectorPerformance = [
    { name: 'IT', change: +1.8, icon: <Smartphone className="w-5 h-5" />, topGainer: 'TCS (+2.1%)', weight: 18.2 },
    { name: 'Pharma', change: +1.4, icon: <Pill className="w-5 h-5" />, topGainer: 'Sun Pharma (+2.8%)', weight: 6.2 },
    { name: 'FMCG', change: +0.9, icon: <ShoppingCart className="w-5 h-5" />, topGainer: 'Britannia (+1.7%)', weight: 7.8 },
    { name: 'Metals', change: +0.6, icon: <Factory className="w-5 h-5" />, topGainer: 'Vedanta (+3.2%)', weight: 5.4 },
    { name: 'Telecom', change: +0.3, icon: <Smartphone className="w-5 h-5" />, topGainer: 'Bharti Airtel (+0.8%)', weight: 4.1 },
    { name: 'Banking', change: -0.2, icon: <Banknote className="w-5 h-5" />, topLoser: 'HDFC Bank (-0.8%)', weight: 32.5 },
    { name: 'Energy', change: -0.5, icon: <Zap className="w-5 h-5" />, topLoser: 'Reliance (-0.7%)', weight: 14.3 },
    { name: 'Automotive', change: -0.8, icon: <Car className="w-5 h-5" />, topLoser: 'Tata Motors (-1.2%)', weight: 8.7 },
    { name: 'Realty', change: -1.2, icon: <Building2 className="w-5 h-5" />, topLoser: 'DLF (-2.1%)', weight: 2.1 }
  ];

  // Market heatmap data - top stocks across all sectors
  const heatmapStocks = [
    { name: 'TCS', sector: 'IT', change: 2.1, weight: 4.2 },
    { name: 'Infosys', sector: 'IT', change: 0.8, weight: 3.8 },
    { name: 'HDFC Bank', sector: 'Banking', change: -0.8, weight: 9.8 },
    { name: 'ICICI Bank', sector: 'Banking', change: 1.4, weight: 8.2 },
    { name: 'Reliance', sector: 'Energy', change: -0.7, weight: 8.9 },
    { name: 'HUL', sector: 'FMCG', change: 0.8, weight: 2.9 },
    { name: 'ITC', sector: 'FMCG', change: 1.2, weight: 2.1 },
    { name: 'SBI', sector: 'Banking', change: 2.1, weight: 3.1 },
    { name: 'Bajaj Finance', sector: 'Banking', change: -1.1, weight: 2.8 },
    { name: 'Maruti', sector: 'Auto', change: 1.4, weight: 2.3 },
    { name: 'Sun Pharma', sector: 'Pharma', change: 2.8, weight: 1.8 },
    { name: 'Wipro', sector: 'IT', change: 0.5, weight: 1.2 },
    { name: 'Tata Steel', sector: 'Metals', change: 2.3, weight: 1.8 },
    { name: 'ONGC', sector: 'Energy', change: 1.9, weight: 1.8 },
    { name: 'Dr Reddy', sector: 'Pharma', change: 0.8, weight: 1.4 },
    { name: 'Kotak Bank', sector: 'Banking', change: -0.5, weight: 2.6 },
    { name: 'Axis Bank', sector: 'Banking', change: 1.7, weight: 2.6 },
    { name: 'M&M', sector: 'Auto', change: 2.1, weight: 1.8 },
    { name: 'Tech Mahindra', sector: 'IT', change: 1.8, weight: 1.1 },
    { name: 'Nestle', sector: 'FMCG', change: 0.5, weight: 1.2 }
  ];

  const getHeatmapColor = (change) => {
    if (change >= 2) return 'bg-green-600';
    if (change >= 1) return 'bg-green-500';
    if (change >= 0.5) return 'bg-green-400';
    if (change >= 0) return 'bg-green-200';
    if (change >= -0.5) return 'bg-red-200';
    if (change >= -1) return 'bg-red-400';
    return 'bg-red-600';
  };

  const getTextColor = (change) => {
    if (Math.abs(change) >= 1) return 'text-white';
    return 'text-gray-800';
  };

  const sectorData = {
    'IT': {
      name: 'Information Technology',
      icon: <Smartphone className="w-6 h-6" />,
      totalWeight: 18.2,
      stocks: [
        { name: 'Tata Consultancy Services', symbol: 'TCS', weight: 4.2, price: '₹3,847', change: '+1.2%' },
        { name: 'Infosys', symbol: 'INFY', weight: 3.8, price: '₹1,678', change: '+0.8%' },
        { name: 'HCL Technologies', symbol: 'HCLTECH', weight: 1.9, price: '₹1,456', change: '-0.3%' },
        { name: 'Wipro', symbol: 'WIPRO', weight: 1.2, price: '₹564', change: '+0.5%' },
        { name: 'Tech Mahindra', symbol: 'TECHM', weight: 1.1, price: '₹1,234', change: '+1.8%' },
        { name: 'LTIMindtree', symbol: 'LTIM', weight: 0.8, price: '₹5,678', change: '+2.1%' },
        { name: 'Persistent Systems', symbol: 'PERSISTENT', weight: 0.6, price: '₹4,321', change: '+1.5%' }
      ]
    },
    'Banking': {
      name: 'Banking & Financial Services',
      icon: <Banknote className="w-6 h-6" />,
      totalWeight: 32.5,
      stocks: [
        { name: 'HDFC Bank', symbol: 'HDFCBANK', weight: 9.8, price: '₹1,687', change: '+0.9%' },
        { name: 'ICICI Bank', symbol: 'ICICIBANK', weight: 8.2, price: '₹1,234', change: '+1.4%' },
        { name: 'State Bank of India', symbol: 'SBIN', weight: 3.1, price: '₹789', change: '+2.1%' },
        { name: 'Kotak Mahindra Bank', symbol: 'KOTAKBANK', weight: 2.8, price: '₹1,876', change: '-0.5%' },
        { name: 'Axis Bank', symbol: 'AXISBANK', weight: 2.6, price: '₹1,098', change: '+1.7%' },
        { name: 'IndusInd Bank', symbol: 'INDUSINDBK', weight: 1.2, price: '₹1,345', change: '+0.8%' },
        { name: 'HDFC Life Insurance', symbol: 'HDFCLIFE', weight: 1.1, price: '₹678', change: '+1.2%' }
      ]
    },
    'Energy': {
      name: 'Oil & Gas',
      icon: <Zap className="w-6 h-6" />,
      totalWeight: 14.3,
      stocks: [
        { name: 'Reliance Industries', symbol: 'RELIANCE', weight: 8.9, price: '₹2,987', change: '+0.7%' },
        { name: 'Oil & Natural Gas Corp', symbol: 'ONGC', weight: 1.8, price: '₹234', change: '+1.9%' },
        { name: 'Indian Oil Corporation', symbol: 'IOC', weight: 1.2, price: '₹156', change: '+2.3%' },
        { name: 'Bharat Petroleum Corp', symbol: 'BPCL', weight: 0.9, price: '₹345', change: '+1.5%' },
        { name: 'Hindustan Petroleum', symbol: 'HINDPETRO', weight: 0.7, price: '₹298', change: '+0.8%' },
        { name: 'GAIL India', symbol: 'GAIL', weight: 0.5, price: '₹187', change: '+1.1%' },
        { name: 'Petronet LNG', symbol: 'PETRONET', weight: 0.3, price: '₹267', change: '+0.6%' }
      ]
    },
    'Automotive': {
      name: 'Automobile & Auto Components',
      icon: <Car className="w-6 h-6" />,
      totalWeight: 8.7,
      stocks: [
        { name: 'Maruti Suzuki India', symbol: 'MARUTI', weight: 2.3, price: '₹11,234', change: '+1.4%' },
        { name: 'Mahindra & Mahindra', symbol: 'M&M', weight: 1.8, price: '₹2,876', change: '+2.1%' },
        { name: 'Tata Motors', symbol: 'TATAMOTORS', weight: 1.6, price: '₹987', change: '+0.9%' },
        { name: 'Bajaj Auto', symbol: 'BAJAJ-AUTO', weight: 1.2, price: '₹8,765', change: '+1.7%' },
        { name: 'Hero MotoCorp', symbol: 'HEROMOTOCO', weight: 0.9, price: '₹4,321', change: '+0.5%' },
        { name: 'Eicher Motors', symbol: 'EICHERMOT', weight: 0.6, price: '₹4,876', change: '+1.3%' },
        { name: 'TVS Motor Company', symbol: 'TVSMOTOR', weight: 0.3, price: '₹2,345', change: '+2.8%' }
      ]
    },
    'Pharma': {
      name: 'Pharmaceuticals',
      icon: <Pill className="w-6 h-6" />,
      totalWeight: 6.2,
      stocks: [
        { name: 'Sun Pharmaceutical', symbol: 'SUNPHARMA', weight: 1.8, price: '₹1,678', change: '+1.2%' },
        { name: 'Dr Reddys Laboratories', symbol: 'DRREDDY', weight: 1.4, price: '₹6,234', change: '+0.8%' },
        { name: 'Cipla', symbol: 'CIPLA', weight: 0.9, price: '₹1,456', change: '+1.5%' },
        { name: 'Divi\'s Laboratories', symbol: 'DIVISLAB', weight: 0.7, price: '₹4,321', change: '+2.1%' },
        { name: 'Biocon', symbol: 'BIOCON', weight: 0.5, price: '₹345', change: '+0.7%' },
        { name: 'Lupin', symbol: 'LUPIN', weight: 0.4, price: '₹1,234', change: '+1.9%' },
        { name: 'Aurobindo Pharma', symbol: 'AUROPHARMA', weight: 0.5, price: '₹987', change: '+1.1%' }
      ]
    },
    'FMCG': {
      name: 'Fast Moving Consumer Goods',
      icon: <ShoppingCart className="w-6 h-6" />,
      totalWeight: 7.8,
      stocks: [
        { name: 'Hindustan Unilever', symbol: 'HINDUNILVR', weight: 2.9, price: '₹2,456', change: '+0.8%' },
        { name: 'ITC', symbol: 'ITC', weight: 2.1, price: '₹456', change: '+1.2%' },
        { name: 'Nestle India', symbol: 'NESTLEIND', weight: 1.2, price: '₹24,567', change: '+0.5%' },
        { name: 'Britannia Industries', symbol: 'BRITANNIA', weight: 0.8, price: '₹4,987', change: '+1.7%' },
        { name: 'Dabur India', symbol: 'DABUR', weight: 0.4, price: '₹567', change: '+0.9%' },
        { name: 'Marico', symbol: 'MARICO', weight: 0.2, price: '₹598', change: '+1.4%' },
        { name: 'Godrej Consumer Products', symbol: 'GODREJCP', weight: 0.2, price: '₹1,234', change: '+0.6%' }
      ]
    },
    'Metals': {
      name: 'Metals & Mining',
      icon: <Factory className="w-6 h-6" />,
      totalWeight: 5.4,
      stocks: [
        { name: 'Tata Steel', symbol: 'TATASTEEL', weight: 1.8, price: '₹134', change: '+2.3%' },
        { name: 'JSW Steel', symbol: 'JSWSTEEL', weight: 1.2, price: '₹876', change: '+1.9%' },
        { name: 'Hindalco Industries', symbol: 'HINDALCO', weight: 0.9, price: '₹567', change: '+2.7%' },
        { name: 'Coal India', symbol: 'COALINDIA', weight: 0.7, price: '₹234', change: '+1.1%' },
        { name: 'Vedanta', symbol: 'VEDL', weight: 0.5, price: '₹456', change: '+3.2%' },
        { name: 'Jindal Steel & Power', symbol: 'JINDALSTEL', weight: 0.2, price: '₹678', change: '+1.8%' },
        { name: 'National Aluminium Co', symbol: 'NATIONALUM', weight: 0.1, price: '₹123', change: '+2.1%' }
      ]
    },
    'Realty': {
      name: 'Real Estate',
      icon: <Building2 className="w-6 h-6" />,
      totalWeight: 2.1,
      stocks: [
        { name: 'DLF', symbol: 'DLF', weight: 0.8, price: '₹789', change: '+1.9%' },
        { name: 'Godrej Properties', symbol: 'GODREJPROP', weight: 0.4, price: '₹2,345', change: '+2.4%' },
        { name: 'Oberoi Realty', symbol: 'OBEROIRLTY', weight: 0.3, price: '₹1,567', change: '+1.7%' },
        { name: 'Brigade Enterprises', symbol: 'BRIGADE', weight: 0.2, price: '₹987', change: '+3.1%' },
        { name: 'Prestige Estates Projects', symbol: 'PRESTIGE', weight: 0.2, price: '₹678', change: '+2.8%' },
        { name: 'Sunteck Realty', symbol: 'SUNTECK', weight: 0.1, price: '₹456', change: '+1.5%' },
        { name: 'Phoenix Mills', symbol: 'PHOENIXLTD', weight: 0.1, price: '₹2,134', change: '+0.9%' }
      ]
    }
  };

  const sectors = Object.keys(sectorData);

  const handleSectorChange = (e) => {
    setSelectedSector(e.target.value);
  };

  const handleIndexClick = (indexName) => {
    // Map index names to Yahoo Finance symbols
    const indexSymbols = {
      'NIFTY 50': '^NSEI',
      'BANK NIFTY': '^NSEBANK',
      'SENSEX': '^BSESN'
    };
    
    const symbol = indexSymbols[indexName];
    if (symbol) {
      const yahooUrl = `https://finance.yahoo.com/quote/${symbol}`;
      window.open(yahooUrl, '_blank');
    }
  };

  const handleSectorClick = (sectorName) => {
    // Find the corresponding sector in sectorData
    const sectorKey = Object.keys(sectorData).find(key => 
      sectorData[key].name.toLowerCase().includes(sectorName.toLowerCase()) ||
      key.toLowerCase() === sectorName.toLowerCase()
    );
    
    if (sectorKey) {
      // Set the selected sector and switch to sectors tab
      setSelectedSector(sectorKey);
      setActiveTab('sectors');
    } else if (sectorName === 'Telecom') {
      // For Telecom, we can show a representative stock
      handleStockClick('BHARTIARTL');
    }
  };

  const handleStockClick = (symbol) => {
    // Convert Indian stock symbols to Yahoo Finance format
    let yahooSymbol = symbol;
    
    // Add .NS suffix for NSE stocks if not already present
    if (!symbol.includes('.')) {
      yahooSymbol = `${symbol}.NS`;
    }
    
    const yahooUrl = `https://finance.yahoo.com/quote/${yahooSymbol}`;
    window.open(yahooUrl, '_blank');
  };

  // Gauge component for scalping view
  const GaugeChart = ({ gainers, losers, total }) => {
    const gainersPercent = (gainers / total) * 100;
    const losersPercent = (losers / total) * 100;
    const gainersAngle = (gainers / total) * 180;
    const losersAngle = (losers / total) * 180;
    
    return (
      <div className="flex flex-col items-center">
        <div className="relative w-80 h-48 mb-6">
          <svg viewBox="0 0 240 120" className="w-full h-full">
            {/* Background arc - light gray */}
            <path
              d="M 40 100 A 80 80 0 0 1 200 100"
              fill="none"
              stroke="#e5e7eb"
              strokeWidth="24"
              strokeLinecap="round"
            />
            
            {/* Gainers arc - green on left */}
            <path
              d={`M 40 100 A 80 80 0 0 1 ${40 + (gainersAngle * 160) / 180} ${100 - 80 * Math.sin((gainersAngle * Math.PI) / 180)}`}
              fill="none"
              stroke="#22c55e"
              strokeWidth="24"
              strokeLinecap="round"
            />
            
            {/* Losers arc - red on right */}
            <path
              d={`M ${200 - (losersAngle * 160) / 180} ${100 - 80 * Math.sin((losersAngle * Math.PI) / 180)} A 80 80 0 0 1 200 100`}
              fill="none"
              stroke="#ef4444"
              strokeWidth="24"
              strokeLinecap="round"
            />
            
            {/* Center text */}
            <text x="120" y="75" textAnchor="middle" className="text-3xl font-bold fill-gray-800">
              {gainers}/{total}
            </text>
          </svg>
          
          {/* Labels */}
          <div className="absolute left-4 bottom-2">
            <div className="text-sm text-green-600 font-semibold">
              Gainers
            </div>
          </div>
          <div className="absolute right-4 bottom-2">
            <div className="text-sm text-red-600 font-semibold">
              Losers
            </div>
          </div>
        </div>
        
        {/* Legend with percentages */}
        <div className="flex gap-8 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-500 rounded"></div>
            <span className="text-gray-600">Gainers:</span>
            <span className="font-semibold text-green-600">{gainers} ({gainersPercent.toFixed(0)}%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-500 rounded"></div>
            <span className="text-gray-600">Losers:</span>
            <span className="font-semibold text-red-600">{losers} ({losersPercent.toFixed(0)}%)</span>
          </div>
        </div>
      </div>
    );
  };

  const selectedSectorData = selectedSector ? sectorData[selectedSector] : null;

  const renderScalpingView = () => (
    <div className="space-y-8">
      {/* Nifty 50 Overview */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">Nifty 50</h2>
          <div className="text-4xl font-bold text-gray-900 mb-2">
            ₹{scalpingData.nifty50.value.toLocaleString()}
          </div>
          <div className="flex items-center justify-center gap-2 text-red-600 mb-4">
            <ArrowDown className="w-5 h-5" />
            <span className="text-xl font-semibold">
              {scalpingData.nifty50.change}({scalpingData.nifty50.changePercent}%)
            </span>
          </div>
          <p className="text-sm text-gray-600">{scalpingData.nifty50.timestamp}</p>
          
          {/* Gauge Chart */}
          <div className="mt-8">
            <GaugeChart 
              gainers={scalpingData.gauge.gainers} 
              losers={scalpingData.gauge.losers} 
              total={scalpingData.gauge.total} 
            />
          </div>
        </div>
      </div>

      {/* Gainers and Losers Tables */}
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Top Gainers */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-green-50 px-6 py-4 border-b">
            <h3 className="text-lg font-semibold text-green-800 flex items-center gap-2">
              <ArrowUp className="w-5 h-5" />
              Top Gainers
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Symbol</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Rate</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Change</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">W Point</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {scalpingData.topGainers.map((stock, index) => (
                  <tr key={stock.symbol} className="hover:bg-green-50 cursor-pointer" onClick={() => handleStockClick(stock.symbol)}>
                    <td className="px-4 py-3 font-medium text-gray-900">{stock.symbol}</td>
                    <td className="px-4 py-3 text-right">₹{stock.rate.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right text-green-600 font-semibold">
                      +{stock.change}({stock.changePercent}%)
                    </td>
                    <td className="px-4 py-3 text-right text-green-600 font-semibold">
                      {stock.weightPoints}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Losers */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-red-50 px-6 py-4 border-b">
            <h3 className="text-lg font-semibold text-red-800 flex items-center gap-2">
              <ArrowDown className="w-5 h-5" />
              Top Losers
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Symbol</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Rate</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Change</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">W Point</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {scalpingData.topLosers.map((stock, index) => (
                  <tr key={stock.symbol} className="hover:bg-red-50 cursor-pointer" onClick={() => handleStockClick(stock.symbol)}>
                    <td className="px-4 py-3 font-medium text-gray-900">{stock.symbol}</td>
                    <td className="px-4 py-3 text-right">₹{stock.rate.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right text-red-600 font-semibold">
                      {stock.change}({stock.changePercent}%)
                    </td>
                    <td className="px-4 py-3 text-right text-red-600 font-semibold">
                      {stock.weightPoints}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="mr-4">
              <svg width="100" height="100" viewBox="0 0 200 200" className="drop-shadow-lg">
                {/* Bull horns - curved and prominent */}
                <path d="M50 60 Q30 40 20 50 Q25 65 45 70 Q50 65 50 60" fill="#4a5568" stroke="#2d3748" strokeWidth="2" />
                <path d="M150 60 Q170 40 180 50 Q175 65 155 70 Q150 65 150 60" fill="#4a5568" stroke="#2d3748" strokeWidth="2" />
                
                {/* Candlesticks with wicks */}
                <g>
                  {/* Green candle 1 */}
                  <line x1="80" y1="70" x2="80" y2="130" stroke="#10b981" strokeWidth="2" />
                  <rect x="70" y="85" width="20" height="35" fill="#10b981" rx="2" />
                  <line x1="80" y1="85" x2="80" y2="70" stroke="#10b981" strokeWidth="2" />
                  <line x1="80" y1="120" x2="80" y2="130" stroke="#10b981" strokeWidth="2" />
                  
                  {/* Red candle */}
                  <line x1="100" y1="65" x2="100" y2="140" stroke="#ef4444" strokeWidth="2" />
                  <rect x="90" y="80" width="20" height="45" fill="#ef4444" rx="2" />
                  <line x1="100" y1="80" x2="100" y2="65" stroke="#ef4444" strokeWidth="2" />
                  <line x1="100" y1="125" x2="100" y2="140" stroke="#ef4444" strokeWidth="2" />
                  
                  {/* Green candle 2 */}
                  <line x1="120" y1="75" x2="120" y2="125" stroke="#10b981" strokeWidth="2" />
                  <rect x="110" y="90" width="20" height="25" fill="#10b981" rx="2" />
                  <line x1="120" y1="90" x2="120" y2="75" stroke="#10b981" strokeWidth="2" />
                  <line x1="120" y1="115" x2="120" y2="125" stroke="#10b981" strokeWidth="2" />
                </g>
                
                {/* Bull body outline (subtle) */}
                <path d="M60 80 Q100 75 140 80 Q140 90 100 95 Q60 90 60 80" fill="none" stroke="#e2e8f0" strokeWidth="1" opacity="0.3" />
              </svg>
            </div>
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold text-blue-600 mb-1">STOCKITY</h1>
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-800">Sector Finder v7.2</h2>
            </div>
          </div>
          <p className="text-lg text-gray-600">Explore stocks and their market contribution across Indian sectors</p>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-8">
          <div className="bg-white rounded-lg shadow-lg p-2 flex gap-2">
            <button
              onClick={() => setActiveTab('sectors')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                activeTab === 'sectors'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Sector Analysis
            </button>
            <button
              onClick={() => setActiveTab('scalping')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center gap-2 ${
                activeTab === 'scalping'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Activity className="w-4 h-4" />
              Live Scalping
            </button>
          </div>
        </div>

        {activeTab === 'scalping' ? renderScalpingView() : (
          <>
        {/* Market Overview Section */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {/* Market Indices */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Market Overview</h3>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar className="w-4 h-4" />
                <span className="hidden sm:inline">{marketOverview.date}</span>
                <span className="sm:hidden">Jul 31</span>
              </div>
            </div>
            
            <div className="space-y-4">
              <div 
                className="flex justify-between items-center p-3 bg-red-50 rounded-lg cursor-pointer hover:bg-red-100 transition-colors"
                onClick={() => handleIndexClick('NIFTY 50')}
                title="Click to view NIFTY 50 chart on Yahoo Finance"
              >
                <div>
                  <div className="text-sm text-gray-600">NIFTY 50</div>
                  <div className="text-xl font-bold text-gray-900">{marketOverview.nifty50.value.toLocaleString()}</div>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-1 text-red-600">
                    <ArrowDown className="w-4 h-4" />
                    <span className="font-semibold">{marketOverview.nifty50.changePercent}%</span>
                  </div>
                  <div className="text-sm text-red-600">{marketOverview.nifty50.change}</div>
                </div>
              </div>

              <div 
                className="flex justify-between items-center p-3 bg-red-50 rounded-lg cursor-pointer hover:bg-red-100 transition-colors"
                onClick={() => handleIndexClick('BANK NIFTY')}
                title="Click to view BANK NIFTY chart on Yahoo Finance"
              >
                <div>
                  <div className="text-sm text-gray-600">BANK NIFTY</div>
                  <div className="text-xl font-bold text-gray-900">{marketOverview.banknifty.value.toLocaleString()}</div>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-1 text-red-600">
                    <ArrowDown className="w-4 h-4" />
                    <span className="font-semibold">{marketOverview.banknifty.changePercent}%</span>
                  </div>
                  <div className="text-sm text-red-600">{marketOverview.banknifty.change}</div>
                </div>
              </div>
              
              <div 
                className="flex justify-between items-center p-3 bg-red-50 rounded-lg cursor-pointer hover:bg-red-100 transition-colors"
                onClick={() => handleIndexClick('SENSEX')}
                title="Click to view SENSEX chart on Yahoo Finance"
              >
                <div>
                  <div className="text-sm text-gray-600">SENSEX</div>
                  <div className="text-xl font-bold text-gray-900">{marketOverview.sensex.value.toLocaleString()}</div>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-1 text-red-600">
                    <ArrowDown className="w-4 h-4" />
                    <span className="font-semibold">{marketOverview.sensex.changePercent}%</span>
                  </div>
                  <div className="text-sm text-red-600">{marketOverview.sensex.change}</div>
                </div>
              </div>
            </div>
            
            <div className="mt-4 text-xs text-gray-500 text-center">
              Last updated: {marketOverview.time}
            </div>
          </div>

          {/* Top Performing Sectors */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Top Gainers</h3>
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            
            <div className="space-y-3">
              {sectorPerformance.filter(s => s.change > 0).slice(0, 5).map((sector, index) => (
                <div 
                  key={sector.name} 
                  className="flex items-center justify-between p-2 bg-green-50 rounded-lg cursor-pointer hover:bg-green-100 transition-colors"
                  onClick={() => handleSectorClick(sector.name)}
                  title={`Click to view ${sector.name} sector stocks`}
                >
                  <div className="flex items-center gap-3">
                    <div className="text-green-600">{sector.icon}</div>
                    <div>
                      <div className="font-medium text-gray-900">{sector.name}</div>
                      <div className="text-xs text-gray-600 hidden sm:block">{sector.topGainer}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-green-600 font-semibold">
                    <ArrowUp className="w-4 h-4" />
                    <span>+{sector.change}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Worst Performing Sectors */}
          <div className="bg-white rounded-xl shadow-lg p-6 sm:col-span-2 lg:col-span-1">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Top Losers</h3>
              <Activity className="w-5 h-5 text-red-600" />
            </div>
            
            <div className="space-y-3">
              {sectorPerformance.filter(s => s.change < 0).slice(0, 5).map((sector, index) => (
                <div 
                  key={sector.name} 
                  className="flex items-center justify-between p-2 bg-red-50 rounded-lg cursor-pointer hover:bg-red-100 transition-colors"
                  onClick={() => handleSectorClick(sector.name)}
                  title={`Click to view ${sector.name} sector stocks`}
                >
                  <div className="flex items-center gap-3">
                    <div className="text-red-600">{sector.icon}</div>
                    <div>
                      <div className="font-medium text-gray-900">{sector.name}</div>
                      <div className="text-xs text-gray-600 hidden sm:block">{sector.topLoser}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-red-600 font-semibold">
                    <ArrowDown className="w-4 h-4" />
                    <span>{sector.change}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Market Heatmap Section */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
            <div className="flex items-center gap-3">
              <BarChart3 className="w-6 h-6 text-purple-600" />
              <h3 className="text-xl font-semibold text-gray-800">Market Heatmap</h3>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-600 rounded"></div>
                <span className="text-gray-600">Strong Gains</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-200 rounded"></div>
                <span className="text-gray-600">Mild Gains</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-200 rounded"></div>
                <span className="text-gray-600">Mild Loss</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-600 rounded"></div>
                <span className="text-gray-600">Strong Loss</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-10 gap-2">
            {heatmapStocks.map((stock, index) => (
              <div
                key={stock.name}
                onClick={() => handleStockClick(stock.name)}
                className={`
                  ${getHeatmapColor(stock.change)} 
                  ${getTextColor(stock.change)}
                  p-2 sm:p-3 rounded-lg cursor-pointer hover:scale-105 transition-all duration-200 hover:shadow-lg
                  border-2 border-transparent hover:border-white
                `}
                style={{ 
                  minHeight: `${Math.max(60, stock.weight * 8)}px`,
                  opacity: 0.9
                }}
                title={`${stock.name} (${stock.sector}) - ${stock.change > 0 ? '+' : ''}${stock.change}% - Weight: ${stock.weight}%`}
              >
                <div className="text-xs font-bold mb-1 leading-tight">{stock.name}</div>
                <div className="text-xs opacity-90 mb-1">{stock.sector}</div>
                <div className="text-xs font-semibold">
                  {stock.change > 0 ? '+' : ''}{stock.change}%
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 text-xs text-gray-500 text-center">
            Box size represents index weight • Click any stock to view detailed charts on Yahoo Finance
          </div>
        </div>

        {/* Sector Selection and Details */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
            <Search className="w-6 h-6 text-blue-600 flex-shrink-0" />
            <div className="flex-1 w-full">
              <label htmlFor="sector-select" className="block text-sm font-medium text-gray-700 mb-2">
                Select Sector
              </label>
              <select
                id="sector-select"
                value={selectedSector}
                onChange={handleSectorChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
              >
                <option value="">Choose a sector...</option>
                {sectors.map(sector => (
                  <option key={sector} value={sector}>
                    {sectorData[sector].name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {selectedSectorData && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg text-white gap-4">
                <div className="flex items-center gap-3">
                  {selectedSectorData.icon}
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold">{selectedSectorData.name}</h2>
                    <p className="text-blue-100">Market Sector Analysis</p>
                  </div>
                </div>
                <div className="text-left sm:text-right">
                  <div className="text-2xl sm:text-3xl font-bold">{selectedSectorData.totalWeight}%</div>
                  <div className="text-blue-100">Total Weight in Nifty</div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
                  <h3 className="text-xl font-semibold text-gray-800">Constituent Stocks</h3>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <TrendingUp className="w-4 h-4" />
                    <span>Live Market Data</span>
                  </div>
                </div>

                <div className="grid gap-3">
                  {selectedSectorData.stocks.map((stock, index) => (
                    <div
                      key={stock.symbol}
                      onClick={() => handleStockClick(stock.symbol)}
                      className="group flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-gray-50 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer hover:shadow-md border-l-4 border-transparent hover:border-blue-500 gap-4"
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <div className="flex-shrink-0 w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                          {index + 1}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                            <span className="truncate">{stock.name}</span>
                            <svg className="w-4 h-4 text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </h4>
                          <p className="text-sm text-gray-600">{stock.symbol}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4 w-full sm:w-auto justify-between sm:justify-end">
                        <div className="text-left sm:text-right">
                          <div className="font-semibold text-gray-900">{stock.price}</div>
                          <div className={`text-sm ${stock.change.startsWith('+') ? 'text-green-600' : 'text-red-600'}`}>
                            {stock.change}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-blue-600">{stock.weight}%</div>
                          <div className="text-xs text-gray-500">Weight</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-semibold text-gray-800">Total Sector Contribution:</span>
                    <span className="text-2xl font-bold text-blue-600">{selectedSectorData.totalWeight}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                    <div
                      className="bg-gradient-to-r from-blue-500 to-purple-600 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(selectedSectorData.totalWeight * 2.5, 100)}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-gray-600">
                    This sector represents {selectedSectorData.totalWeight}% of the total Nifty 50 index weight
                  </p>
                </div>
              </div>
            </div>
          )}

          {!selectedSector && (
            <div className="text-center py-12">
              <div className="w-24 h-24 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                <TrendingUp className="w-12 h-12 text-blue-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Select a Sector to Begin</h3>
              <p className="text-gray-600 max-w-md mx-auto">
                Choose from major Indian market sectors to view detailed stock information and market contributions
              </p>
            </div>
          )}
        </div>
        </>
        )}

        <div className="text-center text-sm text-gray-500">
          <p>Data is indicative and for demonstration purposes. Please consult official sources for investment decisions.</p>
        </div>
      </div>
    </div>
  );
};

export default StockitySectorFinder;